import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { SupportedLocale } from '../../domain/signup';
import { SignupStore } from '../../domain/signup.store';
import { trimmedEmail } from '../trimmed-email';

/**
 * Step four: who will administer the application.
 *
 * **No password field, and there must not be one.** The user is created in the tenant's own realm
 * with an `UPDATE_PASSWORD` required action, so the administrator sets their own from the invitation
 * and whoever fills this form never learns it. A field here would mean one person choosing a
 * password for another and having to transmit it somehow.
 *
 * **No username either.** The email *is* the login name, so asking twice would only create two
 * things to keep in step; the gateway fills Admin's required `username` from it.
 *
 * **This is a different person from the billing party's `billingEmail`**, and the lead paragraph
 * says so. The commonest support question a form like this generates is why two email addresses are
 * wanted, and the answer — invoices go to accounting, the invitation goes to a person — is worth
 * stating before it is asked.
 */
@Component({
  selector: 'pp-administrator-step',
  imports: [ReactiveFormsModule, MatFormField, MatLabel, MatError, MatHint, MatInput, MatSelect, MatOption, TranslocoDirective],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.signup.administrator'">
      <p class="step-lead">{{ t('lead') }}</p>

      <form [formGroup]="form" class="administrator-step-form">
        <mat-form-field>
          <mat-label>{{ t('email') }}</mat-label>
          <input id="signup-admin-email" matInput type="email" formControlName="email" autocomplete="email" />
          <mat-hint>{{ t('emailHint') }}</mat-hint>
          @if (form.controls.email.hasError('required') && form.controls.email.touched) {
            <mat-error>{{ t('emailRequired') }}</mat-error>
          }
          @if (form.controls.email.hasError('email')) {
            <mat-error>{{ t('emailInvalid') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ t('firstName') }}</mat-label>
          <input id="signup-admin-first-name" matInput formControlName="firstName" autocomplete="given-name" />
          @if (form.controls.firstName.hasError('required') && form.controls.firstName.touched) {
            <mat-error>{{ t('firstNameRequired') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ t('lastName') }}</mat-label>
          <input id="signup-admin-last-name" matInput formControlName="lastName" autocomplete="family-name" />
          @if (form.controls.lastName.hasError('required') && form.controls.lastName.touched) {
            <mat-error>{{ t('lastNameRequired') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ t('phone') }}</mat-label>
          <input id="signup-admin-phone" matInput formControlName="phone" autocomplete="tel" />
          <mat-hint>{{ t('phoneHint') }}</mat-hint>
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ t('locale') }}</mat-label>
          <mat-select id="signup-admin-locale" formControlName="defaultLocale">
            @for (locale of locales; track locale) {
              <mat-option [value]="locale">{{ t('locales.' + locale) }}</mat-option>
            }
          </mat-select>
          <mat-hint>{{ t('localeHint') }}</mat-hint>
        </mat-form-field>

        <p class="step-note">{{ t('noPassword') }}</p>
      </form>
    </ng-container>
  `,
  styles: [
    `
      .administrator-step-form {
        display: flex;
        flex-direction: column;
        row-gap: 8px;
        max-width: 460px;
      }

      .step-note {
        font-size: 0.85rem;
        opacity: 0.75;
      }
    `,
  ],
})
export class AdministratorStepComponent implements OnInit {
  private readonly store = inject(SignupStore);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly locales = Object.values(SupportedLocale);

  readonly form = this.formBuilder.nonNullable.group({
    email: ['', [Validators.required, trimmedEmail]],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    phone: [''],
    defaultLocale: [SupportedLocale.EN],
  });

  ngOnInit(): void {
    const administrator = this.store.draft().administrator;
    const organizationLocale = this.store.draft().organization?.defaultLocale as SupportedLocale | undefined;
    this.form.patchValue({
      email: administrator?.email ?? '',
      firstName: administrator?.firstName ?? '',
      lastName: administrator?.lastName ?? '',
      phone: administrator?.phone ?? '',
      // Inherits the organization's language, which is what the contract specifies and what the
      // customer almost always means — they have just chosen it one step earlier.
      defaultLocale: (administrator?.defaultLocale as SupportedLocale | undefined) ?? organizationLocale ?? SupportedLocale.EN,
    });
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.push());
    this.push();
  }

  private push(): void {
    const value = this.form.getRawValue();
    this.store.updateDraft({
      administrator: {
        email: value.email.trim(),
        firstName: value.firstName.trim(),
        lastName: value.lastName.trim(),
        phone: value.phone.trim() || undefined,
        defaultLocale: value.defaultLocale,
      },
    });
  }
}
